
import React, { useState, useMemo } from 'react';
import { useApp } from '../store';
import { Project, ProjectStage, UserRole } from '../types';
// Fix: remove parseISO as it is not available from the installed date-fns version
import { format } from 'date-fns';
import { Search, Filter, ArrowRight, User as UserIcon, Calendar, Clock, ArrowUpDown, ArrowUp, ArrowDown, Paintbrush, Code, Search as QASearch, XCircle } from 'lucide-react';
import { STAGE_CONFIG } from '../constants';
import { ProjectDetailModal } from '../components/ProjectDetailModal';

type SortField = 'client' | 'deadline';
type SortOrder = 'asc' | 'desc';

const ProjectsOverview = () => {
  const { projects, users } = useApp();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredProjects = useMemo(() => {
    // Exclude completed projects as per request
    let list = projects.filter(p => p.stage !== ProjectStage.COMPLETED);

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.clientName.toLowerCase().includes(q));
    }
    if (statusFilter !== 'ALL') {
      list = list.filter(p => p.stage === statusFilter);
    }

    // Apply Sorting
    if (sortField) {
      list = [...list].sort((a, b) => {
        let valA = '';
        let valB = '';

        if (sortField === 'client') {
          valA = a.clientName.toLowerCase();
          valB = b.clientName.toLowerCase();
        } else if (sortField === 'deadline') {
          valA = a.overallDeadline;
          valB = b.overallDeadline;
        }

        if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return list;
  }, [projects, search, statusFilter, sortField, sortOrder]);

  const stats = useMemo(() => {
    return {
      design: projects.filter(p => p.stage === ProjectStage.DESIGN).length,
      dev: projects.filter(p => p.stage === ProjectStage.DEVELOPMENT).length,
      qa: projects.filter(p => p.stage === ProjectStage.QA).length,
    };
  }, [projects]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight uppercase italic">Project Overview</h2>
          <p className="text-slate-500 font-bold mt-1 uppercase tracking-widest text-xs">Complete Active Production Records</p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search Name..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all shadow-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-2xl px-5 py-3 text-xs font-black uppercase tracking-widest focus:outline-none shadow-sm appearance-none min-w-[140px]"
            >
              <option value="ALL">All projects</option>
              {Object.values(ProjectStage).filter(s => s !== ProjectStage.COMPLETED).map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
            {statusFilter !== 'ALL' && (
              <button 
                onClick={() => setStatusFilter('ALL')}
                className="p-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-2xl transition-all border border-red-100 group"
                title="Clear Phase Filter"
              >
                <XCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { 
            label: 'Designing', 
            count: stats.design, 
            stage: ProjectStage.DESIGN, 
            icon: <Paintbrush className="w-5 h-5" />, 
            color: 'text-indigo-600', 
            bg: 'bg-indigo-50' 
          },
          { 
            label: 'Development', 
            count: stats.dev, 
            stage: ProjectStage.DEVELOPMENT, 
            icon: <Code className="w-5 h-5" />, 
            color: 'text-blue-600', 
            bg: 'bg-blue-50' 
          },
          { 
            label: 'Quality Assurance', 
            count: stats.qa, 
            stage: ProjectStage.QA, 
            icon: <QASearch className="w-5 h-5" />, 
            color: 'text-amber-600', 
            bg: 'bg-amber-50' 
          }
        ].map((item) => (
          <button 
            key={item.label}
            onClick={() => setStatusFilter(item.stage)}
            className={`flex items-center justify-between p-6 bg-white rounded-[2rem] border border-slate-200 shadow-sm transition-all hover:shadow-md hover:border-indigo-300 group ${statusFilter === item.stage ? 'ring-2 ring-indigo-500 border-transparent shadow-indigo-100' : ''}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${item.bg} ${item.color} rounded-2xl flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform`}>
                {item.icon}
              </div>
              <div className="text-left">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.label}</p>
                <h4 className="text-2xl font-black text-slate-900">{item.count} <span className="text-[10px] font-bold text-slate-400 ml-1">Projects</span></h4>
              </div>
            </div>
            <ArrowRight className={`w-5 h-5 text-slate-200 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all ${statusFilter === item.stage ? 'text-indigo-500 opacity-100' : ''}`} />
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-[2.5rem] overflow-hidden shadow-sm">
        {filteredProjects.length === 0 ? (
          <div className="p-24 text-center">
            <Clock className="w-16 h-16 text-slate-100 mx-auto mb-6" />
            <h4 className="text-xl font-black text-slate-900 mb-2 uppercase tracking-tight">No Active Projects</h4>
            <p className="text-slate-400 font-medium">Clear filters or initiate new work to populate this view.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th 
                    className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort('client')}
                  >
                    <div className="flex items-center gap-2">
                      Client Name
                      <SortIcon field="client" />
                    </div>
                  </th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Project Name</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status Phase</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Start Date</th>
                  <th 
                    className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort('deadline')}
                  >
                    <div className="flex items-center gap-2">
                      Deadline
                      <SortIcon field="deadline" />
                    </div>
                  </th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Assigned To</th>
                  <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProjects.map(project => {
                  const designer = users.find(u => u.id === project.assignedDesignerId);
                  const dev = users.find(u => u.id === project.assignedDevManagerId);
                  
                  // Logic for "Assigned Lead": Designer in Design, Dev otherwise
                  const lead = project.stage === ProjectStage.DESIGN ? designer : (dev || designer);

                  return (
                    <tr key={project.id} className="hover:bg-slate-50 transition-colors group">
                      <td className="px-8 py-6">
                        <span className="font-black text-indigo-600 uppercase tracking-widest text-xs">
                          {project.clientName}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <p className="font-black text-slate-900 text-lg tracking-tight group-hover:text-indigo-600 transition-colors">{project.name}</p>
                      </td>
                      <td className="px-8 py-6">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setStatusFilter(project.stage);
                          }}
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer hover:ring-2 hover:ring-indigo-300 ${STAGE_CONFIG[project.stage].color}`}
                        >
                          {STAGE_CONFIG[project.stage].label}
                        </button>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2 text-slate-500 font-bold text-xs uppercase tracking-tight">
                          <Calendar className="w-3.5 h-3.5" />
                          {/* Fix: Replace parseISO with native Date constructor */}
                          {format(new Date(project.createdAt), 'MMM d, yyyy')}
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-3.5 h-3.5 ${project.isDelayed ? 'text-red-500' : 'text-slate-300'}`} />
                          <span className={`text-sm font-black ${project.isDelayed ? 'text-red-600' : 'text-slate-700'}`}>
                            {/* Fix: Replace parseISO with native Date constructor */}
                            {format(new Date(project.overallDeadline), 'MMM d, yyyy')}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-3">
                          {lead ? (
                            <>
                              <img src={lead.avatar} className="w-8 h-8 rounded-full border border-slate-200 shadow-sm" alt="" />
                              <div>
                                <p className="text-sm font-black text-slate-900 leading-none">{lead.name}</p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{lead.role.replace('_', ' ')}</p>
                              </div>
                            </>
                          ) : (
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Unassigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button 
                          onClick={() => setSelectedProject(project)}
                          className="p-3 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-2xl transition-all shadow-sm hover:shadow-indigo-100 border border-transparent hover:border-indigo-100"
                        >
                          <ArrowRight className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedProject && (
        <ProjectDetailModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      )}
    </div>
  );
};

export default ProjectsOverview;
